import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Clock, Lock, Headphones, Video } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { fetchSiteSettings } from '../hooks/useSiteData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Variantes d'image ───────────────────────────────────────────────────────
//
// Une carte de 350 px affichait la photo d'origine — jusqu'a 2 560 px et
// 961 Ko pour le heros de la home. `/api/img` produit a la demande une version
// WebP a la bonne largeur, mise en cache sur disque. Les images hebergees
// ailleurs (Unsplash, placeholder) sont laissees telles quelles : on ne
// redimensionne que ce que l'on sert.
const VARIANT_WIDTHS = [240, 480, 720, 960, 1600];

const isLocalMedia = (url) => typeof url === 'string' && url.startsWith('/media/');

export const variantUrl = (url, width) =>
  `${API_URL}/api/img?src=${encodeURIComponent(url)}&w=${width}&fmt=webp`;

/** srcSet, sizes et src pour une image locale ; rien pour une image externe.
 *
 *  `src` est reecrit lui aussi : laisser l'original en repli suffisait a ce
 *  que le navigateur le telecharge en plus de la variante — 961 Ko pour rien.
 *  Un navigateur sans srcset recoit alors une variante moyenne, pas le fichier
 *  d'origine. A poser APRES le `src` d'origine dans le JSX pour l'emporter. */
export const responsiveImg = (url, sizes, widths = VARIANT_WIDTHS) => {
  if (!isLocalMedia(url)) return {};
  const fallbackWidth = widths[Math.min(2, widths.length - 1)];
  return {
    srcSet: widths.map(w => `${variantUrl(url, w)} ${w}w`).join(', '),
    sizes,
    src: variantUrl(url, fallbackWidth),
  };
};


// Cache hero_layout settings to avoid re-fetching on each render
let _heroLayoutCache = null;
let _heroLayoutPromise = null;
const getHeroLayout = () => {
  if (_heroLayoutCache) return _heroLayoutCache;
  if (!_heroLayoutPromise) {
    _heroLayoutPromise = fetchSiteSettings()
      .then(d => { _heroLayoutCache = d?.hero_layout || {}; return _heroLayoutCache; })
      .catch(() => ({}));
  }
  return null;
};

// ─── Hero Card (inchangé) ──────────────────────────────────────────────────────

export const ArticleCardHero = ({ article }) => {
  const [hl, setHl] = useState(_heroLayoutCache || {});

  useEffect(() => {
    const cached = getHeroLayout();
    if (cached) { setHl(cached); return; }
    _heroLayoutPromise?.then(data => setHl(data || {}));
  }, []);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const overlayOpacity = (hl.overlay_opacity ?? 80) / 100;

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-xl overflow-hidden"
      data-testid={`article-hero-${article.id}`}
    >
      {/* IMAGE — titre + badges ancrés en bas, rien d'autre */}
      <div className="aspect-[9/14] sm:aspect-[16/9] md:aspect-[16/8] lg:aspect-[16/7] relative overflow-hidden">
        <img
          src={article.cover_image || 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=1200'}
          {...responsiveImg(article.cover_image, '(min-width: 1024px) 760px, 100vw')}
          alt={article.title}
          /* Cette image est le plus grand element de la page : la charger en
             paresseux revenait a demander le LCP en dernier. */
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="1600"
          height="900"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.srcset = '';
            e.target.src = 'https://placehold.co/600x400/1f2937/6b7280?text=InfoFlow';
          }}
        />
        {/* Gradient overlay — tiers inférieur pour lisibilité titre */}
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(to top, rgba(0,0,0,${Math.min(overlayOpacity * 0.6 + 0.05, 0.7)}) 0%, rgba(0,0,0,${overlayOpacity * 0.2}) 30%, transparent 60%)` }}
        />

        {/* Logo overlay */}
        {hl.show_logo && hl._logo_url && (
          <img
            src={hl._logo_url}
            alt=""
            className="absolute"
            style={{
              width: `${hl.logo_size || 48}px`,
              height: `${hl.logo_size || 48}px`,
              objectFit: 'contain',
              ...(hl.logo_position === 'top-right' ? { top: '16px', right: '16px' } :
                 hl.logo_position === 'bottom-left' ? { bottom: '80px', left: '16px' } :
                 hl.logo_position === 'bottom-right' ? { bottom: '80px', right: '16px' } :
                 { top: '16px', left: '16px' })
            }}
          />
        )}

        {/* Badges + Titre — ancrés tout en bas de l'image */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            {article.is_breaking && (
              <Badge className="bg-[#D62828] text-white border-0 rounded-md">BREAKING</Badge>
            )}
            {article.category_name && (
              <Badge variant="outline" className="text-white border-white/50 uppercase text-xs tracking-wider rounded-md">
                {article.category_name}
              </Badge>
            )}
            {article.is_premium && (
              <Badge className="bg-[#E9C46A] text-black border-0 rounded-md">
                <Lock className="h-3 w-3 mr-1" />
                PREMIUM
              </Badge>
            )}
            {article.content_format === 'audio' && (
              <Badge className="bg-amber-100 text-amber-900 border-0 rounded-md">
                <Headphones className="h-3 w-3 mr-1" />
                PODCAST
              </Badge>
            )}
            {article.content_format === 'video' && (
              <Badge className="bg-sky-100 text-sky-900 border-0 rounded-md">
                <Video className="h-3 w-3 mr-1" />
                VIDÉO
              </Badge>
            )}
          </div>

          <h2 className="text-lg md:text-2xl lg:text-3xl font-serif font-bold text-white leading-snug line-clamp-2 group-hover:underline decoration-2 underline-offset-4">
            {article.title}
          </h2>
        </div>
      </div>

      {/* CHAPEAU — hors de l'image, masqué sur mobile */}
      <div className="hidden sm:block px-4 md:px-6 pt-4 pb-2">
        {article.excerpt && (
          <p className="text-muted-foreground text-base md:text-lg line-clamp-2 max-w-3xl">
            {article.excerpt}
          </p>
        )}

        {/* Métadonnées */}
        {(hl.show_meta !== false) && (
          <div className="flex items-center gap-4 text-muted-foreground/70 text-sm mt-3">
            <span>{formatDate(article.created_at)}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {article.reading_time} min
            </span>
            {article.audio_url && <Headphones className="h-4 w-4" />}
            {article.video_url && <Video className="h-4 w-4" />}
          </div>
        )}
      </div>
    </Link>
  );
};

// ─── Live Card (inchangé) ──────────────────────────────────────────────────────

export const ArticleCardLive = ({ article }) => {
  const formatTime = (dateStr) => {
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  return (
    <Link
      to={`/article/${article.id}`}
      className="flex items-start gap-3 py-3 border-b border-border/30 last:border-0 group hover:bg-muted/50 transition-colors px-2 rounded-lg"
      data-testid={`article-live-${article.id}`}
    >
      <span className="flex-shrink-0 bg-[#D62828] text-white text-xs font-bold px-2 py-1 rounded-md min-w-[50px] text-center">
        {formatTime(article.created_at)}
      </span>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium leading-snug group-hover:text-[#D62828] transition-colors line-clamp-3">
          {article.title}
        </h4>
      </div>
    </Link>
  );
};

// ─── Premium Card (refonte) ────────────────────────────────────────────────────

const FALLBACK_IMG = 'https://placehold.co/600x400/1f2937/6b7280?text=InfoFlow';

export const ArticleCard = ({ article, variant = 'default' }) => {

  // ── Compact variant (sidebar / list) ─────────────────────────────────────
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="flex gap-4 items-start py-4 border-b border-border/30 last:border-0 group"
        data-testid={`article-compact-${article.id}`}
      >
        <div className="flex-1 min-w-0">
          <h3 className="font-serif font-bold text-[15px] leading-snug group-hover:text-[#D62828] transition-colors duration-200 line-clamp-3">
            {article.title}
          </h3>
        </div>
        {article.cover_image && (
          <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-lg">
            <img
              src={article.cover_image}
              {...responsiveImg(article.cover_image, '80px', [240])}
              alt=""
              loading="lazy"
              decoding="async"
              width="80"
              height="80"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.target.onerror = null; e.target.srcset = ''; e.target.src = FALLBACK_IMG; }}
            />
          </div>
        )}
      </Link>
    );
  }

  // ── Grid variant (featured row sous le hero) ─────────────────────────────
  if (variant === 'grid') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block"
        data-testid={`article-grid-${article.id}`}
      >
        <div className="aspect-[4/3] overflow-hidden rounded-xl">
          <img
            src={article.cover_image || 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=600'}
            {...responsiveImg(article.cover_image, '(min-width: 1024px) 240px, 45vw', [240, 480, 960])}
            alt={article.title}
            loading="lazy"
            decoding="async"
            width="480"
            height="360"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            onError={(e) => { e.target.onerror = null; e.target.srcset = ''; e.target.src = FALLBACK_IMG; }}
          />
        </div>
        {/* Titre sous l'image — image libérée */}
        <h3 className="mt-2 font-serif font-bold text-foreground text-base leading-snug line-clamp-2 group-hover:text-[#D62828] transition-colors">
          {article.title}
        </h3>
      </Link>
    );
  }

  // ── Default variant — Premium Editorial ──────────────────────────────────
  //
  // Image + gradient + titre en overlay. Rien d'autre.
  // Inspiré Apple News / FT / médias premium.

  const hasImage = !!article.cover_image;

  if (hasImage) {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block"
        data-testid={`article-card-${article.id}`}
      >
        <div className="aspect-[3/2] relative overflow-hidden rounded-xl">
          <img
            src={article.cover_image}
            {...responsiveImg(article.cover_image, '(min-width: 1024px) 380px, 100vw')}
            alt={article.title}
            loading="lazy"
            decoding="async"
            width="960"
            height="640"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            onError={(e) => { e.target.onerror = null; e.target.srcset = ''; e.target.src = FALLBACK_IMG; }}
          />

          {/* Badges légers en overlay haut — image libérée, sans gradient sombre */}
          {article.is_premium && (
            <div className="absolute top-3 right-3 pointer-events-none">
              <Badge className="bg-[#E9C46A]/90 text-black border-0 rounded-md shadow-lg backdrop-blur-sm text-[10px] font-bold px-2 py-0.5">
                <Lock className="h-2.5 w-2.5 mr-1" />
                PREMIUM
              </Badge>
            </div>
          )}
          {article.is_breaking && (
            <div className="absolute top-3 left-3 pointer-events-none">
              <Badge className="bg-[#D62828] text-white border-0 rounded-md shadow-lg text-[10px] font-bold px-2 py-0.5">
                LIVE
              </Badge>
            </div>
          )}
        </div>

        {/* Titre + extrait sous l'image */}
        <h3 className="mt-2.5 font-serif font-bold text-foreground text-lg sm:text-xl leading-snug line-clamp-2 group-hover:text-[#D62828] transition-colors">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
            {article.excerpt}
          </p>
        )}
      </Link>
    );
  }

  // ── Fallback sans image — fond sombre épuré ──────────────────────────────

  return (
    <Link
      to={`/article/${article.id}`}
      className="group relative block overflow-hidden rounded-xl"
      data-testid={`article-card-${article.id}`}
    >
      <div className="aspect-[3/2] relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />

        {article.is_premium && (
          <div className="absolute top-3 right-3 pointer-events-none">
            <Badge className="bg-[#E9C46A]/90 text-black border-0 rounded-md text-[10px] font-bold px-2 py-0.5">
              <Lock className="h-2.5 w-2.5 mr-1" />
              PREMIUM
            </Badge>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 pointer-events-none">
          <h3 className="font-serif font-bold text-white text-lg sm:text-xl md:text-[22px] leading-[1.25] line-clamp-3">
            {article.title}
          </h3>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
